import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { ArrowRight, Calendar, Tag } from "lucide-react";

// Inline markdown-lite: **bold**
function renderInline(text, keyPrefix) {
  return text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((p, j) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={`${keyPrefix}-${j}`} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
      : <span key={`${keyPrefix}-${j}`}>{p}</span>
  );
}

// Block markdown-lite: paragraphs, "- " bullets, "1." numbered lists, "# " headings
function renderContent(text) {
  const lines = (text || "").split("\n");
  const blocks = [];
  let list = null; // { type: "ul" | "ol", items: [] }

  const flushList = () => {
    if (!list) return;
    const Tag = list.type;
    blocks.push(
      <Tag
        key={`list-${blocks.length}`}
        className={`my-4 space-y-2 pl-5 text-slate-700 leading-relaxed ${list.type === "ol" ? "list-decimal" : "list-disc"}`}
      >
        {list.items.map((it, k) => <li key={k}>{renderInline(it, `li-${blocks.length}-${k}`)}</li>)}
      </Tag>
    );
    list = null;
  };

  lines.forEach((raw, i) => {
    const ln = raw.trim();
    if (!ln) { flushList(); return; }

    const bullet = ln.match(/^[-*]\s+(.*)$/);
    const numbered = ln.match(/^\d+[.)]\s+(.*)$/);
    const heading = ln.match(/^(#{1,3})\s+(.*)$/);

    if (bullet) {
      if (!list || list.type !== "ul") { flushList(); list = { type: "ul", items: [] }; }
      list.items.push(bullet[1]);
      return;
    }
    if (numbered) {
      if (!list || list.type !== "ol") { flushList(); list = { type: "ol", items: [] }; }
      list.items.push(numbered[1]);
      return;
    }
    flushList();
    if (heading) {
      const level = heading[1].length;
      const cls = level === 1 ? "text-2xl" : level === 2 ? "text-xl" : "text-lg";
      blocks.push(
        <h3 key={`h-${i}`} className={`h-display ${cls} text-slate-900 mt-8 mb-3`}>
          {renderInline(heading[2], `h-${i}`)}
        </h3>
      );
      return;
    }
    blocks.push(
      <p key={`p-${i}`} className="mb-4 text-slate-700 leading-relaxed">{renderInline(ln, `p-${i}`)}</p>
    );
  });
  flushList();
  return blocks;
}

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setBlog(null); setNotFound(false);
    api.get(`/blogs/${slug}`).then((r) => {
      setBlog(r.data);
      // Try same-category first, fall back to latest other posts so the section always renders
      api.get(`/blogs?category=${encodeURIComponent(r.data.category)}`).then((rs) => {
        const same = rs.data.filter((x) => x.slug !== slug);
        if (same.length >= 3) {
          setRelated(same.slice(0, 3));
        } else {
          api.get(`/blogs`).then((all) => {
            const others = all.data.filter((x) => x.slug !== slug);
            const seen = new Set(same.map((x) => x.slug));
            const merged = [...same, ...others.filter((x) => !seen.has(x.slug))].slice(0, 3);
            setRelated(merged);
          });
        }
      });
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <PublicLayout><div className="max-w-3xl mx-auto px-6 py-24 text-center"><h1 className="h-display text-4xl text-slate-900">Post not found</h1><Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-blue-700">Back to blog <ArrowRight size={14} /></Link></div></PublicLayout>;
  if (!blog) return <PublicLayout><div className="max-w-3xl mx-auto px-6 py-24 text-center text-slate-500">Loading…</div></PublicLayout>;

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";

  return (
    <PublicLayout>
      <article className="max-w-3xl mx-auto px-6 lg:px-10 pt-12 pb-16">
        <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-1 text-sm text-blue-700 hover:text-blue-900">← Back to all posts</Link>
        <div className="mt-6 text-xs uppercase tracking-widest text-blue-700">{blog.category}</div>
        <h1 className="h-display mt-3 text-4xl md:text-5xl text-slate-900 leading-tight">{blog.title}</h1>
        <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><Calendar size={12} /> {fmt(blog.published_at || blog.created_at)}</span>
          <span>By {blog.author}</span>
        </div>
        {blog.cover_image && (
          <div className="mt-8 aspect-[16/9] overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
            <img src={blog.cover_image} alt={blog.title} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="mt-8 text-lg text-slate-700 leading-relaxed italic border-l-2 border-blue-700 pl-4">{blog.excerpt}</div>
        <div className="mt-8 prose prose-slate max-w-none">{renderContent(blog.content || "")}</div>
        {(blog.tags || []).length > 0 && (
          <div className="mt-10 flex flex-wrap gap-2">
            {blog.tags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 text-xs text-slate-600 border border-slate-200 rounded-full px-3 py-1"><Tag size={10} /> {t}</span>
            ))}
          </div>
        )}
      </article>

      {related.length > 0 && (
        <section className="bg-slate-50 border-t border-slate-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
            <h3 className="h-display text-3xl text-slate-900">Related reads</h3>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((b) => (
                <Link key={b.id} to={`/blog/${b.slug}`} className="rounded-2xl bg-white border border-slate-200 p-6 hover:border-blue-400 hover:-translate-y-0.5 transition-transform duration-300">
                  <div className="text-xs uppercase tracking-widest text-blue-700">{b.category}</div>
                  <h4 className="mt-2 font-medium text-slate-900">{b.title}</h4>
                  <p className="mt-3 text-sm text-slate-600 line-clamp-2">{b.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </PublicLayout>
  );
}
